import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { authApi, ApiError, type AdminUser } from "@/lib/api";
import { onUnauthorized } from "@/lib/api/client";

export type AuthState =
  | { status: "loading" }
  | { status: "unauthenticated" }
  | { status: "session_expired" }
  | {
      status: "totp_challenge";
      challengeId: string;
      expiresAt: string;
      email: string;
      requiresEnrollment: boolean;
    }
  | { status: "authenticated"; user: AdminUser };

interface AuthContextValue {
  state: AuthState;
  /** Starts password login. Resolves once a TOTP challenge is pending. */
  submitLogin: (email: string, password: string) => Promise<void>;
  submitTotpCode: (code: string) => Promise<void>;
  submitRecoveryCode: (code: string) => Promise<void>;
  /** Begins TOTP enrollment from the current password-verified challenge. */
  beginEnrollment: () => Promise<{ enrollmentId: string; otpauthUri: string; qrImage: string }>;
  confirmEnrollment: (enrollmentId: string, code: string) => Promise<string[]>;
  /**
   * docs/API_CONTRACT.md does not state whether confirming enrollment alone
   * establishes a full session, so we probe `/auth/me` after the operator
   * has safely stored their recovery codes. If a session already exists we
   * go straight to the dashboard; otherwise we stay on the TOTP challenge so
   * the operator enters one more fresh code through the normal verify step.
   */
  checkSessionAfterEnrollment: () => Promise<"authenticated" | "needs_verification">;
  logout: () => Promise<void>;
  /** Returns to the plain login form, discarding any in-progress challenge. */
  resetToLogin: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    authApi
      .getMe()
      .then((me) => {
        if (!cancelled) setState({ status: "authenticated", user: me.user });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "unauthenticated" });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return onUnauthorized(() => {
      setState((current) =>
        current.status === "authenticated" ? { status: "session_expired" } : current,
      );
    });
  }, []);

  const submitLogin = useCallback(async (email: string, password: string) => {
    const challenge = await authApi.login({ email, password });
    setState({
      status: "totp_challenge",
      challengeId: challenge.challengeId,
      expiresAt: challenge.expiresAt,
      email,
      requiresEnrollment: challenge.requiresEnrollment,
    });
  }, []);

  const submitTotpCode = useCallback(
    async (code: string) => {
      if (state.status !== "totp_challenge") {
        throw new ApiError(400, "invalid_state", "No hay un inicio de sesion en curso.", undefined, null);
      }
      const result = await authApi.verifyTotp({ challengeId: state.challengeId, code });
      setState({ status: "authenticated", user: result.user });
    },
    [state],
  );

  const submitRecoveryCode = useCallback(
    async (code: string) => {
      if (state.status !== "totp_challenge") {
        throw new ApiError(400, "invalid_state", "No hay un inicio de sesion en curso.", undefined, null);
      }
      const result = await authApi.verifyRecoveryCode({
        challengeId: state.challengeId,
        recoveryCode: code,
      });
      setState({ status: "authenticated", user: result.user });
    },
    [state],
  );

  const beginEnrollment = useCallback(async () => {
    if (state.status !== "totp_challenge") {
      throw new ApiError(400, "invalid_state", "No hay un inicio de sesion en curso.", undefined, null);
    }
    return authApi.enrollTotp(state.challengeId);
  }, [state]);

  const confirmEnrollment = useCallback(
    async (enrollmentId: string, code: string) => {
      if (state.status !== "totp_challenge") {
        throw new ApiError(400, "invalid_state", "No hay un inicio de sesion en curso.", undefined, null);
      }
      const result = await authApi.confirmTotp({
        enrollmentId,
        code,
        challengeId: state.challengeId,
      });
      return result.recoveryCodes;
    },
    [state],
  );

  const checkSessionAfterEnrollment = useCallback(async () => {
    try {
      const me = await authApi.getMe();
      setState({ status: "authenticated", user: me.user });
      return "authenticated" as const;
    } catch {
      return "needs_verification" as const;
    }
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setState({ status: "unauthenticated" });
  }, []);

  const resetToLogin = useCallback(() => {
    setState({ status: "unauthenticated" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      submitLogin,
      submitTotpCode,
      submitRecoveryCode,
      beginEnrollment,
      confirmEnrollment,
      checkSessionAfterEnrollment,
      logout,
      resetToLogin,
    }),
    [
      state,
      submitLogin,
      submitTotpCode,
      submitRecoveryCode,
      beginEnrollment,
      confirmEnrollment,
      checkSessionAfterEnrollment,
      logout,
      resetToLogin,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
